/// <amd-module name="Favorites/Dropdown/ArtifactDropdown" />
import * as FavoriteResources from "Favorites/Common/Resources";
import * as React from "react";
import { ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { IButtonProps } from "VSSUI/Button";
import { Dropdown, DropdownCallout, IDropdownCalloutProps, IDropdownPivot, IWithPivotsChildProps, WithPivots } from "VSSUI/Dropdown";
import { Icon } from "VSSUI/Icon";
import { IListSelection } from "VSSUI/List";
import { IListBoxItem, ListBoxItemType } from "VSSUI/ListBox";
import { Tooltip } from "VSSUI/TooltipEx";
import { css } from "VSSUI/Util";
import { AggregateItemProvider } from "VSSUI/Utilities/AggregateItemProvider";
import { IFocusable } from "VSSUI/Utilities/Focus";
import { Location } from "VSSUI/Utilities/Position";
import { IArtifactDropdownProps } from "./ArtifactDropdown.Props";
import { ArtifactDropdownProvider } from "./ArtifactDropdownProvider";

export class ArtifactDropdown<TArtifact = {}> extends React.Component<IArtifactDropdownProps<TArtifact>> implements IFocusable<{}> {
    /**
     * Optional list of actions to render after all items and groups
     */
    public get actions(): IButtonProps[] {
        const { browseAllText, onBrowseAllClick, otherActions } = this.props;

        const actions: IButtonProps[] = otherActions ? [...otherActions] : [];
        if (onBrowseAllClick) {
            // add browse all action to the end if a click handler specified
            actions.push({
                onClick: onBrowseAllClick,
                text: browseAllText || FavoriteResources.FavoriteItemPickerBrowseAllText,
                iconProps: {
                    iconName: "Home"
                }
            });
        }

        return actions;
    }
    private provider = new AggregateItemProvider<IListBoxItem<TArtifact>>();
    private selectedPivot: ObservableValue<string>;
    private searchResults = new ObservableArray<IListBoxItem<TArtifact>>();

    private dropdown = React.createRef<Dropdown>();

    public constructor(props: IArtifactDropdownProps<TArtifact>) {
        super(props);

        this.selectedPivot = props.selectedPivot || new ObservableValue<string>(props.pivots ? props.pivots[0].id : "");
        this.provider.push(props.items);
        this.provider.push(this.searchResults);
        if (props.searchResultsGroupName) {
            this.searchResults.push({ id: "search-results-header", text: props.searchResultsGroupName, type: ListBoxItemType.Header });
        }
    }

    public render() {
        const {
            ariaLabel,
            calloutContentClassName,
            className,
            showFilterBox,
            filterPlaceholderText,
            filteredNoResultsText,
            filteredResultsLoadingText,
            loading,
            noItemsText,
            onFilterTextChanged,
            placeholder,
            pivots,
            renderExpandable,
            searching,
            selection,
            showItemsWhileSearching,
            userFilteredItems,
            width
        } = this.props;
        const anchorOrigin = { horizontal: Location.start, vertical: Location.end };
        const dropdownOrigin = { horizontal: Location.start, vertical: Location.start };
        const dropdown = (
            <Dropdown
                ariaLabel={ariaLabel}
                calloutContentClassName={calloutContentClassName}
                className={className}
                filterThrottleWait={100}
                ref={this.dropdown}
                actions={this.actions}
                items={this.provider}
                loading={loading}
                onExpand={this.onExpand}
                onSelect={this.onSelect}
                onCollapse={this.onCollapse}
                noItemsText={noItemsText}
                placeholder={placeholder}
                renderCallout={(props: IDropdownCalloutProps) => (
                    <DropdownCallout
                        {...props}
                        anchorOrigin={anchorOrigin}
                        dropdownOrigin={dropdownOrigin}
                        filterPlaceholderText={filterPlaceholderText}
                        filteredNoResultsText={filteredNoResultsText}
                        filteredResultsLoadingText={filteredResultsLoadingText}
                        onFilterTextChanged={(e, value) => {
                            props.onFilterTextChanged && props.onFilterTextChanged(e, value);
                            onFilterTextChanged && onFilterTextChanged(e, value);
                        }}
                    />
                )}
                renderExpandable={
                    renderExpandable ? props => renderExpandable({ ...props, renderSelectedItems: this.renderSelectedItems }) : undefined
                }
                searching={searching}
                selection={selection}
                showFilterBox={showFilterBox !== false}
                showItemsWhileSearching={showItemsWhileSearching}
                userFilteredItems={userFilteredItems}
                width={width}
            />
        );
        if (pivots) {
            return (
                <WithPivots
                    selectedPivot={this.selectedPivot}
                    onPivotClicked={this.onPivotClicked}
                    pivots={pivots}
                    calloutProps={{ anchorOrigin, dropdownOrigin, onFilterTextChanged }}
                >
                    {(props: IWithPivotsChildProps) => React.cloneElement(dropdown, props)}
                </WithPivots>
            );
        } else {
            return dropdown;
        }
    }

    public componentWillUnmount() {
        this.provider.collections.forEach(collection => {
            if (collection.provider && (collection.provider as ArtifactDropdownProvider<TArtifact>).dispose) {
                (collection.provider as ArtifactDropdownProvider<TArtifact>).dispose();
            }
        });
    }

    public expand() {
        if (this.dropdown.current) {
            this.dropdown.current.expand();
        }
    }

    public focus() {
        if (this.dropdown.current) {
            this.dropdown.current.focus();
        }
    }

    private onExpand = () => {
        this.provider.collections.forEach(collection => {
            const artifactProvider = collection.provider as ArtifactDropdownProvider<TArtifact>;
            if (artifactProvider.getItems) {
                artifactProvider.getItems();
            }
        });
    };

    private onSelect = (event: React.SyntheticEvent<HTMLElement, Event>, item: IListBoxItem<TArtifact>) => {
        if (item.data && this.props.getArtifactHref) {
            window.location.href = this.props.getArtifactHref(item.data);
        }
        if (this.props.onSelect) {
            this.props.onSelect(event, item);
        }
    };

    private onCollapse = () => {
        if (this.props.onCollapse) {
            this.props.onCollapse();
        }
    };
    
    private renderSelectedItems = (selection: IListSelection, items: IListBoxItem<TArtifact>[]) => {
        const selectedIndex = selection.value[0].beginIndex;
        
        return renderSelectedItem(
            items[selectedIndex],
            selection.selectedCount
        );
    };

    private onPivotClicked(pivot: IDropdownPivot<TArtifact>) {
        this.selectedPivot.value = pivot.id;
    }
}

function getRootItemTextUtil<TArtifact>(rootItem: IListBoxItem<TArtifact>, count?: number) : string{
        if(!count || count == 1) return rootItem.text ?? "";

        if (count > 1) return rootItem.text + " (+" + (count - 1).toString() + ")"
        else return "";
}

export function renderSelectedItem<TArtifact>(item: IListBoxItem<TArtifact>, count?:number, getRootItemText: (selectedItem: IListBoxItem<TArtifact>, count?: number) => string = getRootItemTextUtil) {
    let text = getRootItemText(item, count);
    return (
        <div className="flex-row">
            {item.iconProps && <Icon {...item.iconProps} className={css(item.iconProps.className, "artifact-dropdown-icon")} />}
            <Tooltip overflowOnly={true}>
                <span className="text-ellipsis">{text}</span>
            </Tooltip>
        </div>
    );
}
